import React, { useCallback, useContext } from "react";
import {
  graphql,
  loadQuery,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
} from "react-relay";
import { WTCS_SELECT_TASKS_QUERY } from "./WTCSSelectTasks";
import { WTCSStackScreenProps } from "./WTCSStack";
import { AHSeparator, ListItem, useBoxStyle } from "arkh";
import { keyExtractorId } from "../../../WidgetAdd/utils/keyExtractors";

import { WTCSSelectRoutineQuery } from "./__generated__/WTCSSelectRoutineQuery.graphql";
import { WTCSSelectTasksQuery } from "./__generated__/WTCSSelectTasksQuery.graphql";
import { FlatList } from "react-native";
import { WidgetTaskCollectionContext } from "../WidgetTaskCollectionContext";

export const WTCS_SELECT_ROUTINE_QUERY = graphql`
  query WTCSSelectRoutineQuery($teamId: ID!) {
    store {
      team(id: $teamId) {
        id
        ...ListSectionHeaderTeam_Team
        routines(includeOrg: true, type: STANDARD, onlyChecklists: true) {
          id
          title
          activeSections {
            id
            lastVersion {
              tasks {
                id
                title
              }
            }
          }
        }
      }
    }
  }
`;

export function WTCSSelectRoutines(
  props: WTCSStackScreenProps<"WTCSSelectRoutine">
) {
  const env = useRelayEnvironment();

  const [queryRef] = useQueryLoader(
    WTCS_SELECT_ROUTINE_QUERY,
    props.route.params.query
  );

  if (!queryRef) {
    throw new Error("WATaskCollectionSettingsRoutines: queryRef is null");
  }
  const data = usePreloadedQuery<WTCSSelectRoutineQuery>(
    WTCS_SELECT_ROUTINE_QUERY,
    queryRef
  );

  const onPressItem = useCallback(
    (itemId: string) => {
      const qr = loadQuery<WTCSSelectTasksQuery>(
        env,
        WTCS_SELECT_TASKS_QUERY,
        {
          routineId: itemId,
        },
        {
          fetchPolicy: "store-and-network",
        }
      );
      props.navigation.navigateDeprecated("WTCSSelectTasks", {
        queryRef: qr,
      });
    },
    [env, props.navigation]
  );

  const context = useContext(WidgetTaskCollectionContext);

  const onSelectRoutine = useCallback(
    (item: { id: string; sectionId: string; routineTitle: string }) => {
      context.setSelectedTasks((prevTasks) => {
        const isSelected = prevTasks.some(
          (t) => t.id === item.id && t.sectionId === item.sectionId
        );

        if (isSelected) {
          return prevTasks.filter(
            (t) => !(t.id === item.id && t.sectionId === item.sectionId)
          );
        } else {
          if (prevTasks.length >= 8) {
            return prevTasks;
          }
          return [
            ...prevTasks,
            {
              id: item.id,
              sectionId: item.sectionId,
              ordinal: prevTasks.length,
              title: "",
              sectionTitle: "",
              routineTitle: item.routineTitle,
            },
          ];
        }
      });
    },
    [context]
  );

  const routines = data.store.team?.routines ?? [];

  const selectedTasks = context.selectedTasks;

  type Item = (typeof routines)[number];

  const renderItem = useCallback(
    ({ item, index }: { item: Item; index: number }) => {
      const isSingle =
        item.activeSections.length === 1 &&
        item.activeSections[0]?.lastVersion.tasks.length === 1 &&
        item.activeSections[0]?.lastVersion.tasks[0].title.length === 0
          ? {
              sectionId: item.activeSections[0].id,
              taskId: item.activeSections[0].lastVersion.tasks[0].id,
            }
          : false;

      const isSelected =
        isSingle &&
        selectedTasks.some(
          (t) => t.id === isSingle.taskId && t.sectionId === isSingle.sectionId
        );
      return (
        <ListItem
          border="border-primary"
          onPress={() => {
            if (isSingle) {
              onSelectRoutine({
                id: isSingle.taskId,
                sectionId: isSingle.sectionId,
                routineTitle: item.title,
              });
            } else {
              onPressItem(item.id);
            }
          }}
          isFirst={index === 0}
          isLast={index === (routines.length ?? 0) - 1}
          right={
            isSingle ? (
              <ListItem.MultiSelectIndicator selected={isSelected} />
            ) : (
              <ListItem.NavArrow />
            )
          }
        >
          <ListItem.Title>{item.title}</ListItem.Title>
        </ListItem>
      );
    },
    [onPressItem, routines.length, selectedTasks, onSelectRoutine]
  );

  const contentContainerStyle = useBoxStyle({
    space: "medium",
  });

  return (
    <FlatList
      style={{ flex: 1 }}
      data={routines}
      renderItem={renderItem}
      ItemSeparatorComponent={AHSeparator}
      contentContainerStyle={contentContainerStyle}
      keyExtractor={keyExtractorId}
    />
  );
}
